import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { jwtDecode } from "jwt-decode";
import { ApiContext } from "../../context/MyContext";
import { get } from "../../service/comms/commsApi";
import {
  getAnalyticsCoaches,
  classifyOversightError,
} from "../../service/comms/oversightAnalyticsService";

/**
 * Runtime access-gating provider for oversight/analytics surfaces.
 *
 * The backend is the sole source of truth for who counts as
 * executive/manager tier: this provider never infers a role from local
 * data, it only classifies the outcome of a single cheap probe request
 * (`GET .../oversight/analytics/coaches?limit=1`) via
 * `classifyOversightError`:
 *
 *   - 200 payload                  -> staff
 *   - 403 ("forbidden")            -> notStaff (terminal for this identity —
 *     a real access decision from the backend).
 *   - 502/504/network ("gateway")  -> gatewayDown (retryable via `retry()`)
 *   - anything else ("other", e.g. a lingering 401 or a 500) -> notStaffSoft.
 *     This is a deliberate fail-closed choice: an ambiguous error should
 *     never leave oversight UI visible, so it renders exactly like
 *     notStaff. Unlike a real 403 though, it isn't a trustworthy access
 *     decision (it's as likely to be a stale/expired token or a flaky
 *     backend), so it recovers via an explicit `reprobe()`/`retry()` or an
 *     identity change instead of being sticky for the rest of the session.
 *
 * State machine: idle -> loading -> staff | notStaff | notStaffSoft | gatewayDown.
 * `loading` (as read from `useOversightAccess()`) is true for both idle and
 * loading, so consumers can treat "not yet known" as a single loading state.
 * `isStaff` is only true in the `staff` state; both notStaff and
 * notStaffSoft render as "not staff" to consumers.
 *
 * Two things keep this state from going sticky in ways that don't reflect
 * reality:
 *
 *  1. Token validity, not just presence, gates every probe (mount effect
 *     and `ensureProbe()`), mirroring `PrivateRoute`'s `jwtDecode` + `exp`
 *     check. `msAgentsInstance` has no refresh interceptor, so probing with
 *     an expired token would 401 -> classify as "other" -> stick the whole
 *     session at notStaffSoft. An expired/absent token instead just leaves
 *     state at idle.
 *  2. The authenticated identity (`isAuthenticated` + `empId` from
 *     `ApiContext` — this provider is mounted inside `ApiProvider`'s tree,
 *     see App.js) is tracked against the identity that produced the
 *     current probe result. Login/logout/switching users don't reload this
 *     SPA, so without this the provider would keep showing a previous
 *     user's staff verdict. When the identity moves on, state resets to
 *     idle; that flips `ensureProbe`'s identity, which re-triggers the
 *     `[ensureProbe]` mount effects already wired up in
 *     OversightRoute/AppSidebar, causing a fresh probe for the new identity.
 *
 * The provider never blocks rendering — children render immediately
 * regardless of probe state.
 */

const OversightAccessState = {
  IDLE: "idle",
  LOADING: "loading",
  STAFF: "staff",
  NOT_STAFF: "notStaff",
  NOT_STAFF_SOFT: "notStaffSoft",
  GATEWAY_DOWN: "gatewayDown",
};

const OversightAccessContext = createContext({
  isStaff: false,
  loading: true,
  gatewayDown: false,
  retry: () => {},
  reprobe: () => {},
  ensureProbe: () => {},
});

/**
 * @returns {{
 *   isStaff: boolean,
 *   loading: boolean,
 *   gatewayDown: boolean,
 *   retry: Function,
 *   reprobe: Function,
 *   ensureProbe: Function,
 * }}
 */
export const useOversightAccess = () => useContext(OversightAccessContext);

/**
 * True only when `access_token` exists AND decodes to a not-yet-expired
 * JWT. Mirrors `PrivateRoute`'s `isTokenExpired` check (same `jwtDecode` +
 * `exp` comparison) so this provider never probes on a token that would
 * 401 against msAgentsInstance (no refresh interceptor there).
 * @returns {boolean}
 */
function hasValidAccessToken() {
  const token = localStorage.getItem("access_token");
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    if (!decoded.exp) return false;
    return decoded.exp > Date.now() / 1000;
  } catch {
    return false;
  }
}

export function OversightAccessProvider({ children }) {
  const [state, setState] = useState(OversightAccessState.IDLE);

  // Mirror of `state` for stable callbacks (ensureProbe must NOT change
  // identity on state transitions — consumers wire it into `[ensureProbe]`
  // effects, and a state-dependent identity would re-fire those effects on
  // every transition, turning a persistent soft failure into an unbounded
  // probe loop).
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Identity of the currently authenticated user as far as this provider
  // is concerned. Changes across login (anon -> auth:<empId>), logout
  // (auth:<empId> -> anon), and — the case this exists for — logging in as
  // a *different* user without an SPA reload (auth:<empId> -> auth:<other>).
  const apiContext = useContext(ApiContext);
  const empId = apiContext?.empId ?? null;
  const isAuthenticated = !!apiContext?.isAuthenticated;
  const identity = `${isAuthenticated ? "auth" : "anon"}:${empId ?? ""}`;
  const identityRef = useRef(identity);
  useEffect(() => {
    identityRef.current = identity;
  }, [identity]);

  // Guards against overlapping probes — both StrictMode's double-invoked
  // mount effect and a consumer calling ensureProbe/retry/reprobe while one
  // is already in flight. Set synchronously (before the first `await`), so
  // the second synchronous StrictMode invocation always sees it as true.
  const probeInFlightRef = useRef(false);
  // Identity that was current when the most recent probe was kicked off;
  // null until the first probe ever runs. Compared against `identity`
  // below to detect a stale result after a login/logout/user-switch.
  const probedIdentityRef = useRef(null);

  const probe = useCallback(async () => {
    if (probeInFlightRef.current) {
      return;
    }
    probeInFlightRef.current = true;
    probedIdentityRef.current = identityRef.current;
    setState(OversightAccessState.LOADING);
    try {
      await getAnalyticsCoaches(get, { limit: 1 });
      setState(OversightAccessState.STAFF);
    } catch (err) {
      const classification = classifyOversightError(err);
      if (classification === "gateway") {
        setState(OversightAccessState.GATEWAY_DOWN);
      } else if (classification === "forbidden") {
        // A real access decision from the backend — terminal for this
        // identity (see module doc comment above).
        setState(OversightAccessState.NOT_STAFF);
      } else {
        // 'other' (lingering 401, 500, ...) fail-closes to the same
        // rendering outcome but recovers via reprobe()/retry() or an
        // identity change (never auto-retried — see ensureProbe).
        setState(OversightAccessState.NOT_STAFF_SOFT);
      }
    } finally {
      probeInFlightRef.current = false;
    }
  }, []);

  // Mount-time probe, gated on a valid (present + unexpired) access token
  // so we never probe while unauthenticated (login -> app transition) or
  // against a stale token. If no valid token exists yet, state stays idle
  // and `ensureProbe()` (below) is the consumer-driven way to probe once
  // one shows up.
  useEffect(() => {
    if (hasValidAccessToken()) {
      probe();
    }
  }, [probe]);

  // Reset (and directly re-probe) whenever the authenticated identity moves
  // on from the one that produced the current probe result. The probe is
  // issued here, not via consumer effects — `ensureProbe` is deliberately
  // identity-stable, so consumers' `[ensureProbe]` effects won't re-fire on
  // state changes.
  //
  // Hydration exception: after a full page load, `empId` is populated
  // asynchronously from localStorage, so `identity` moves "auth:" ->
  // "auth:<empId>" for the SAME user. That transition just adopts the new
  // identity string without resetting, avoiding a duplicate probe on every
  // reload (and the equivalent multi-step transitions during login).
  useEffect(() => {
    const prev = probedIdentityRef.current;
    if (prev === null || prev === identity) return;
    const [prevAuth, prevEmp] = prev.split(":");
    const [nextAuth, nextEmp] = identity.split(":");
    const isSameUserHydration =
      prevAuth === "auth" &&
      nextAuth === "auth" &&
      (prevEmp === "" || nextEmp === "");
    if (isSameUserHydration) {
      probedIdentityRef.current = identity;
      return;
    }
    probedIdentityRef.current = null;
    setState(OversightAccessState.IDLE);
    if (hasValidAccessToken()) {
      probe();
    }
  }, [identity, probe]);

  const retry = useCallback(() => {
    probe();
  }, [probe]);

  const reprobe = useCallback(() => {
    probe();
  }, [probe]);

  const ensureProbe = useCallback(() => {
    // Probes only from idle (never probed / just reset). NOT_STAFF_SOFT is
    // deliberately NOT auto-reprobed here: consumers call ensureProbe from
    // `[ensureProbe]` effects, and auto-retrying soft failures from those
    // would loop unboundedly while a non-403/502 backend error persists.
    // Soft failures recover via an explicit reprobe()/retry() or an
    // identity change. Identity-stable by design (state read via ref).
    if (stateRef.current === OversightAccessState.IDLE && hasValidAccessToken()) {
      probe();
    }
  }, [probe]);

  const value = useMemo(() => {
    const loading =
      state === OversightAccessState.IDLE || state === OversightAccessState.LOADING;
    return {
      isStaff: state === OversightAccessState.STAFF,
      loading,
      gatewayDown: state === OversightAccessState.GATEWAY_DOWN,
      retry,
      reprobe,
      ensureProbe,
    };
  }, [state, retry, reprobe, ensureProbe]);

  return (
    <OversightAccessContext.Provider value={value}>
      {children}
    </OversightAccessContext.Provider>
  );
}
